import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select, { MultiValue } from "react-select";
import { toast } from "react-toastify";

import Breadcrumbs from "../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../Common/ShimmerLoader";
import { getAllAgeCategoriesPaginated } from "../../../api/tournament/ageCategoryService";
import { Fixture, listFixtures } from "../../../api/tournament/fixturesService";
import { getTournamentById, getTournamentSeasons } from "../../../api/tournament/manageTournamentService";
import { AccessType, canGenerateIvsCredentials, createVideo, generateIvsCredentials, getVideo, ManualStatus, SflTvVideo, updateVideo, VideoCategory, VideoPayload, VideoType } from "../../../api/tournament/videoService";
import { createVideoAssetSequence, getVideoAssetSequence, toStoredVideoAssetKey, uploadSflTvVideoAsset } from "../../../api/tournament/videoUploadService";
import { toCdnUrl } from "../../../api/fileUploadService";
import { buildVideoDateTime, buildVideoPricing, getInrPrice, normalizeVideoAccessType, toDateInputValue, toTimeInputValue } from "./videoForm";
import "./Videos.css";

type Mode = "create" | "view" | "edit";
const urlFields = ["youtubeUrl"] as const;
const videoTypes: VideoType[] = ["live_stream", "highlight"];
const videoCategories: VideoCategory[] = ["match_coverage", "ceremony", "award", "other"];
const manualStatuses: ManualStatus[] = ["upcoming", "live", "past"];
const accessTypes: AccessType[] = ["free", "paid", "exclusive_paid"];
type MatchOption = { value: string; label: string; sport: string };

const sideLabel = (fixture: Fixture, side: "one" | "two") => {
  const team = side === "one" ? fixture.teamA : fixture.teamB;
  const sideData = side === "one" ? fixture.side1 : fixture.side2;
  const legacy = side === "one" ? fixture.team1Info : fixture.team2Info;
  const playerNames = sideData?.players?.map((player) => player.name).filter(Boolean)
    || legacy?.players?.map((player) => player.name).filter(Boolean);

  if (playerNames?.length) return playerNames.join(" / ");
  if (team?.name) return team.name;
  if (sideData?.team && typeof sideData.team === "object") return sideData.team.name;
  if (legacy?.name) return legacy.name;
  return "TBD";
};

const fixtureLabel = (fixture: Fixture) => [
  `Match ${fixture.matchNum}`,
  `${sideLabel(fixture, "one")} vs ${sideLabel(fixture, "two")}`,
  fixture.date,
  fixture.venue,
].filter(Boolean).join(" · ");

const VideoDetail: React.FC<{ mode: Mode }> = ({ mode }) => {
  const { tournamentId, seasonId, videoId } = useParams<{ tournamentId: string; seasonId: string; videoId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const readOnly = mode === "view";
  const fallbackPath = `/manage-tournaments-fixtures/${tournamentId}/seasons/${seasonId}`;
  const returnPath = (location.state as any)?.returnPath || fallbackPath;
  const [form, setForm] = useState<VideoPayload>({ tournament: tournamentId || "", tournamentSeason: seasonId || "", date: "", type: "live_stream", title: "", sortOrder: 0, isActive: true, accessType: "free" });
  const [time, setTime] = useState("");
  const [price, setPrice] = useState("");
  const [video, setVideo] = useState<SflTvVideo | null>(null);
  const [loading, setLoading] = useState(mode !== "create");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedVideo, setGeneratedVideo] = useState<SflTvVideo | null>(null);
  const [tournamentName, setTournamentName] = useState("Tournament");
  const [seasonName, setSeasonName] = useState("Season");
  const [sport, setSport] = useState("Football");
  const [ageCategories, setAgeCategories] = useState<any[]>([]);
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [loadingFixtures, setLoadingFixtures] = useState(false);
  const [assetSequence, setAssetSequence] = useState("");
  const [uploadingAsset, setUploadingAsset] = useState<"video" | "thumbnail" | null>(null);
  const parentSport = (location.state as any)?.selectedTournament?.sports || "Football";

  const back = () => navigate(returnPath, { state: { ...(location.state as any), activeTab: "videos" } });
  const load = useCallback(async () => {
    if (!videoId) return;
    setLoading(true);
    try {
      const result = await getVideo(videoId);
      if (result.tournament !== tournamentId || result.tournamentSeason !== seasonId) {
        setError("This video does not belong to the selected tournament season.");
        return;
      }
      const linkedMatches = Array.isArray(result.linkedMatches)
        ? result.linkedMatches.map((linkedMatch: any) => ({
          matchId: linkedMatch?.matchId || "",
          sport: linkedMatch?.sport || linkedMatch?.sports || "",
        }))
        : [];
      setVideo({ ...result, linkedMatches });
      const {
        _id,
        liveStreamInfo,
        streamRecordingInfo,
        createdAt,
        updatedAt,
        linkedMatch,
        linkedMatches: _linkedMatches,
        monetizationProduct,
        ...writable
      } = result as SflTvVideo & { linkedMatch?: unknown };
      setForm({
        ...writable,
        date: toDateInputValue(result.date),
        accessType: normalizeVideoAccessType(result.accessType),
        videoUrl: toStoredVideoAssetKey(result.videoUrl),
        thumbnailUrl: toStoredVideoAssetKey(result.thumbnailUrl),
        ...(linkedMatches.length ? { linkedMatches } : {}),
      });
      setTime(toTimeInputValue(result.dateTime));
      setPrice(getInrPrice(result.pricing));
      setAssetSequence(getVideoAssetSequence(result.videoUrl) || getVideoAssetSequence(result.thumbnailUrl));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [videoId, tournamentId, seasonId]);

  useEffect(() => { if (mode !== "create") load(); }, [mode, load]);
  useEffect(() => {
    if (!tournamentId) return;
    getTournamentById(tournamentId, parentSport).then(async (tournament) => {
      setTournamentName(tournament.name || "Tournament");
      setSport(tournament.sports || "Football");
      const seasons = await getTournamentSeasons(tournamentId, tournament.sports || "Football");
      const season = seasons.find((item) => item._id === seasonId);
      setSeasonName(season?.seasonLabel || season?.seasonYearRange || "Season");
    }).catch(() => undefined);
  }, [tournamentId, seasonId, parentSport]);
  useEffect(() => {
    if (!tournamentId || !seasonId) return;
    getAllAgeCategoriesPaginated(tournamentId, 1, 100, "", "", "", seasonId, sport)
      .then((result) => setAgeCategories(result?.paginatedResults || result?.data?.paginatedResults || []))
      .catch(() => setAgeCategories([]));
  }, [tournamentId, seasonId, sport]);
  useEffect(() => {
    if (!tournamentId || !seasonId) return;
    let isCurrent = true;
    setLoadingFixtures(true);
    const loadFixtures = async () => {
      try {
        const firstPage = await listFixtures({
          tournamentId,
          tournamentSeason: seasonId,
          sport,
          page: 1,
          pageSize: 50,
        });
        const remainingPages = await Promise.all(
          Array.from({ length: Math.max(0, firstPage.totalPages - 1) }, (_, index) => listFixtures({
            tournamentId,
            tournamentSeason: seasonId,
            sport,
            page: index + 2,
            pageSize: 50,
          })),
        );
        if (isCurrent) setFixtures([
          ...(firstPage.paginatedResults || []),
          ...remainingPages.flatMap((page) => page.paginatedResults || []),
        ]);
      } catch {
        if (isCurrent) setFixtures([]);
      } finally {
        if (isCurrent) setLoadingFixtures(false);
      }
    };
    loadFixtures();
    return () => { isCurrent = false; };
  }, [tournamentId, seasonId, sport]);

  const set = <K extends keyof VideoPayload>(key: K, value: VideoPayload[K]) => setForm((previous) => ({ ...previous, [key]: value }));
  const setVideoType = (type: VideoType) => setForm((previous) => type === "live_stream" ? { ...previous, type, videoUrl: undefined, duration: undefined } : { ...previous, type });
  const setCategory = (category?: VideoPayload["category"]) => setForm((previous) => ({
    ...previous,
    category,
  }));
  const uploadAsset = async (asset: "video" | "thumbnail", file?: File) => {
    if (!file || !tournamentId || !seasonId) return;
    const sequence = assetSequence || createVideoAssetSequence();
    setAssetSequence(sequence);
    setUploadingAsset(asset);
    setError(null);
    try {
      const key = await uploadSflTvVideoAsset({
        file,
        tournamentId,
        seasonId,
        sequence,
        asset,
      });
      if (asset === "video") {
        setForm((previous) => ({ ...previous, videoUrl: toStoredVideoAssetKey(key) }));
      } else {
        setForm((previous) => ({ ...previous, thumbnailUrl: toStoredVideoAssetKey(key) }));
      }
    } catch (err: any) {
      setError(err?.message || `The ${asset} could not be uploaded.`);
    } finally {
      setUploadingAsset(null);
    }
  };
  const onAssetFileChange = (asset: "video" | "thumbnail") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      void uploadAsset(asset, event.target.files?.[0]);
      event.target.value = "";
    };
  const matchOptions: MatchOption[] = fixtures.map((fixture) => ({
    value: fixture._id,
    label: fixtureLabel(fixture),
    sport: fixture.sports || sport,
  }));
  const selectedMatches = matchOptions.filter((option) =>
    form.linkedMatches?.some((linkedMatch) => linkedMatch.matchId === option.value),
  );
  const selectMatches = (options: MultiValue<MatchOption>) => set(
    "linkedMatches",
    options.map((option) => ({ matchId: option.value, sport: option.sport })),
  );
  const validate = () => {
    if (!tournamentId || !seasonId || !form.tournament || !form.tournamentSeason) return "Tournament and tournament season are required.";
    if (!form.type) return "Type is required.";
    if (!videoTypes.includes(form.type)) return "Type must be live stream or highlight.";
    if (!form.title?.trim()) return "Title is required.";
    if (!form.date) return "Date is required.";
    const parsedDate = new Date(`${form.date}T00:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date) || Number.isNaN(parsedDate.getTime()) || parsedDate.toISOString().slice(0, 10) !== form.date) return "Date must use a valid YYYY-MM-DD value.";
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)) return "Time is required.";
    if (form.category && !videoCategories.includes(form.category)) return "Category is invalid.";
    if (form.manualStatus && !manualStatuses.includes(form.manualStatus)) return "Manual status is invalid.";
    if (form.accessType !== "inherit" && !accessTypes.includes(form.accessType)) return "Access type is invalid.";
    if (form.accessType === "exclusive_paid" && (!price.trim() || !Number.isFinite(Number(price)) || Number(price) <= 0)) return "An INR price greater than zero is required for exclusive paid videos.";
    if (form.duration !== undefined && (Number(form.duration) < 0 || !Number.isFinite(Number(form.duration)))) return "Duration must be a non-negative number.";
    for (const key of urlFields) if (form[key] && !/^https?:\/\//.test(form[key] as string)) return `${key} must be a valid http(s) URL.`;
    if (form.linkedMatches?.some(({ matchId, sport: linkedSport }) => !matchId?.trim() || !linkedSport?.trim())) return "Each linked match must include a match and sport.";
    if (form.linkedMatches && new Set(form.linkedMatches.map(({ matchId }) => matchId)).size !== form.linkedMatches.length) return "A match can only be linked once.";
    return null;
  };
  const save = async () => {
    const message = validate();
    if (message) return setError(message);
    setSaving(true); setError(null);
    try {
      const { linkedMatches, dateTime: _dateTime, pricing: _pricing, ...videoFields } = form;
      const pricing = buildVideoPricing(form.accessType, price);
      const payload = {
        ...videoFields,
        tournament: tournamentId!,
        tournamentSeason: seasonId!,
        dateTime: buildVideoDateTime(form.date, time),
        title: form.title.trim(),
        section: form.section?.trim() || null,
        ...(pricing ? { pricing } : {}),
        ...(linkedMatches?.length
          ? { linkedMatches }
          : video?.linkedMatches?.length
            ? { linkedMatches: [] }
            : {}),
      };
      const saved = mode === "create" ? await createVideo(payload) : await updateVideo(videoId!, payload);
      toast.success(mode === "create" ? "Video created successfully." : "Video updated successfully.");
      navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${seasonId}/videos/${saved._id}`, { state: location.state });
    } catch (err: any) { setError(err.message); } finally { setSaving(false); }
  };
  const provision = async () => {
    if (!videoId || !video || !canGenerateIvsCredentials(video)) {
      setError("Only an unprovisioned live stream can generate IVS credentials.");
      return;
    }
    setSaving(true);
    try { setGeneratedVideo(await generateIvsCredentials(videoId)); toast.success("IVS credentials generated."); await load(); }
    catch (err: any) { setError(err.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="page-content"><Container fluid><ShimmerLoader type="form" rows={8} /></Container></div>;
  const field = (label: string, key: keyof VideoPayload, type = "text") => <Form.Group className="mb-3"><Form.Label>{label}</Form.Label><Form.Control type={type} value={(form[key] ?? "") as string | number} readOnly={readOnly} onChange={(e) => set(key, (type === "number" ? (e.target.value === "" ? undefined : Number(e.target.value)) : e.target.value) as VideoPayload[typeof key])} /></Form.Group>;
  const systemEntries = Object.entries(video?.liveStreamInfo || {});
  const videoFileName = form.videoUrl?.split(/[?#]/)[0].split("/").pop();

  return <div className="page-content"><Container fluid>
    <div className="mb-3 px-3"><Breadcrumbs items={[{ label: tournamentName, onClick: back }, { label: seasonName, onClick: back }, { label: "Videos", onClick: back }, { label: mode === "create" ? "Create Video" : mode === "edit" ? "Edit Video" : "View Video", active: true }]} /></div>
    <div className="d-flex justify-content-between align-items-center mb-4 px-3"><h1 className="sfltv-title">{mode === "create" ? "Create Video" : mode === "edit" ? "Edit Video" : "Video Details"}</h1><Button variant="light" onClick={back}><i className="ph-arrow-left me-1" /> Back</Button></div>
    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
    <Row><Col lg={8}><Card className="sfltv-detail-card"><Card.Body><h5 className="mb-3">Video information</h5><Row>
      <Col md={6}><Form.Group className="mb-3"><Form.Label>Tournament</Form.Label><Form.Control value={tournamentName} readOnly /></Form.Group></Col>
      <Col md={6}><Form.Group className="mb-3"><Form.Label>Tournament season</Form.Label><Form.Control value={seasonName} readOnly /></Form.Group></Col>
      <Col md={6}>{field("Date *", "date", "date")}</Col><Col md={6}><Form.Group className="mb-3"><Form.Label>Time *</Form.Label><Form.Control aria-label="Time" type="time" value={time} readOnly={readOnly} onChange={(e) => setTime(e.target.value)} /></Form.Group></Col>
      <Col md={6}><Form.Group className="mb-3"><Form.Label>Type *</Form.Label><Form.Select disabled={readOnly} value={form.type} onChange={(e) => setVideoType(e.target.value as VideoType)}><option value="live_stream">Live stream</option><option value="highlight">Highlight</option></Form.Select></Form.Group></Col>
      <Col md={6}><Form.Group className="mb-3"><Form.Label>Category</Form.Label><Form.Select disabled={readOnly} value={form.category || ""} onChange={(e) => setCategory((e.target.value || undefined) as VideoPayload["category"])}><option value="">Not specified</option><option value="match_coverage">Match coverage</option><option value="ceremony">Ceremony</option><option value="award">Award</option><option value="other">Other</option></Form.Select></Form.Group></Col><Col md={6}><Form.Group className="mb-3"><Form.Label>Age category</Form.Label><Form.Select disabled={readOnly} value={form.ageCategory || ""} onChange={(e) => set("ageCategory", e.target.value || undefined)}><option value="">Not specified</option>{ageCategories.map((item) => <option key={item._id} value={item._id}>{item.name || item.ageCategoryName || item.ageCategory || item._id}</option>)}</Form.Select></Form.Group></Col>
      <Col md={12}><Form.Group className="mb-3"><Form.Label>Linked matches</Form.Label><Select inputId="linked-matches" aria-label="Linked matches" isMulti isDisabled={readOnly} isClearable={!readOnly} isLoading={loadingFixtures} options={matchOptions} value={selectedMatches} onChange={selectMatches} placeholder="Optional: link this video to matches" noOptionsMessage={() => "No matches found for this season"} /><Form.Text>Selected videos are shown on each linked match.</Form.Text>{form.linkedMatches?.length && selectedMatches.length !== form.linkedMatches.length && <Form.Text className="d-block text-warning">One or more selected matches are not available in this season.</Form.Text>}</Form.Group></Col>
      <Col md={6}>{field("Title *", "title")}</Col><Col md={6}>{field("Subtitle", "subtitle")}</Col><Col md={6}><Form.Group className="mb-3"><Form.Label>Section</Form.Label><Form.Control value={form.section || ""} readOnly={readOnly} onChange={(e) => set("section", e.target.value)} /><Form.Text>Use the same section on videos that should appear together publicly.</Form.Text></Form.Group></Col><Col md={6}>{field("Venue", "venue")}</Col>
      <Col md={6}><Form.Group className="mb-3"><Form.Label>Upload thumbnail</Form.Label><Form.Control aria-label="Upload thumbnail" type="file" accept="image/jpeg,image/png,image/webp" disabled={readOnly || !!uploadingAsset} onChange={onAssetFileChange("thumbnail")} />{uploadingAsset === "thumbnail" && <Spinner className="mt-2" animation="border" size="sm" role="status" aria-label="Uploading thumbnail" />}{form.thumbnailUrl && <img className="d-block mt-2 border rounded" src={toCdnUrl(form.thumbnailUrl)} alt="Thumbnail preview" style={{ width: 160, height: 90, objectFit: "cover" }} />}</Form.Group></Col>
      <Col md={6}>{field("YouTube URL", "youtubeUrl")}</Col>{form.type === "highlight" && <><Col md={6}><Form.Group className="mb-3"><Form.Label>Upload video</Form.Label><Form.Control aria-label="Upload video" type="file" accept="video/mp4" disabled={readOnly || !!uploadingAsset} onChange={onAssetFileChange("video")} />{uploadingAsset === "video" && <Spinner className="mt-2" animation="border" size="sm" role="status" aria-label="Uploading video" />}{form.videoUrl && <Form.Text className="d-block mt-1">Uploaded video: <a href={toCdnUrl(form.videoUrl)} target="_blank" rel="noreferrer">{videoFileName}</a></Form.Text>}</Form.Group></Col><Col md={4}>{field("Duration (seconds)", "duration", "number")}</Col></>}<Col md={4}>{field("Sort order", "sortOrder", "number")}</Col>
      <Col md={4}><Form.Group className="mb-3"><Form.Label>Manual status</Form.Label><Form.Select disabled={readOnly} value={form.manualStatus || ""} onChange={(e) => set("manualStatus", (e.target.value || undefined) as VideoPayload["manualStatus"])}><option value="">Not specified</option><option value="upcoming">Upcoming</option><option value="live">Live</option><option value="past">Past</option></Form.Select></Form.Group></Col>
      <Col md={4}><Form.Group className="mb-3"><Form.Label>Access type</Form.Label><Form.Select aria-label="Access type" disabled={readOnly} value={form.accessType} onChange={(e) => set("accessType", e.target.value as VideoPayload["accessType"])}>{form.accessType === "inherit" && <option value="inherit" disabled>Inherit (legacy)</option>}<option value="free">Free</option><option value="paid">Paid</option><option value="exclusive_paid">Exclusive paid</option></Form.Select></Form.Group></Col>
      {form.accessType === "exclusive_paid" && <Col md={4}><Form.Group className="mb-3"><Form.Label>Price (INR)</Form.Label><Form.Control aria-label="Price (INR)" type="number" min="0.01" step="0.01" value={price} readOnly={readOnly} onChange={(e) => setPrice(e.target.value)} /></Form.Group></Col>}
      <Col md={12}><Form.Check type="switch" label="Active" checked={form.isActive} disabled={readOnly} onChange={(e) => set("isActive", e.target.checked)} /></Col>
    </Row>{!readOnly && <div className="mt-4"><Button onClick={save} disabled={saving || !!uploadingAsset}>{saving ? "Saving..." : "Save Video"}</Button></div>}</Card.Body></Card></Col>
    <Col lg={4}>{mode !== "create" && <Card className="sfltv-detail-card"><Card.Body><div className="d-flex justify-content-between"><h5>IVS details</h5>{video && canGenerateIvsCredentials(video) && <Button size="sm" onClick={provision} disabled={saving}>Generate IVS Credentials</Button>}</div>{systemEntries.length ? systemEntries.map(([key, value]) => <div className="mb-2" key={key}><label>{key}</label><div className="sfltv-readonly">{String(value ?? "-")}</div></div>) : <p className="text-muted mb-0">IVS credentials have not been generated.</p>}</Card.Body></Card>}
    {mode !== "create" && <Card className="sfltv-detail-card mt-3"><Card.Body><h5>Recording details</h5>{Object.entries(video?.streamRecordingInfo || {}).map(([key, value]) => <div className="mb-2" key={key}><label>{key}</label><div className="sfltv-readonly">{Array.isArray(value) ? value.join(", ") || "-" : String(value ?? "-")}</div></div>)}</Card.Body></Card>}</Col></Row>
    {generatedVideo && <div className="modal show d-block sfltv-credentials-backdrop" role="dialog"><div className="modal-dialog modal-dialog-centered"><div className="modal-content"><div className="modal-header"><h5>IVS Credentials</h5><button className="btn-close" onClick={() => setGeneratedVideo(null)} /></div><div className="modal-body">{Object.entries(generatedVideo.liveStreamInfo || {}).map(([key, value]) => <div className="sfltv-credential" key={key}><span>{key}</span><code>{String(value ?? "-")}</code></div>)}</div><div className="modal-footer"><Button onClick={() => setGeneratedVideo(null)}>Close</Button></div></div></div></div>}
  </Container></div>;
};

export default VideoDetail;
